import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { KPL_LOGO, mobileNavLinks, navLinks } from "../../constants/siteContent";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toTestId = (label) => label.toLowerCase().replace(/\s+/g, "-");

  const scrollToSection = (href) => {
    const target = document.querySelector(href);
    if (target) {
      const navOffset = 96;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: targetTop, behavior: "smooth" });
    }
  };

  const handleSectionClick = (href) => (event) => {
    event.preventDefault();

    if (href === "#") {
      setMobileMenuOpen(false);
      return;
    }

    if (!href.startsWith("#")) {
      setMobileMenuOpen(false);
      navigate(href);
      return;
    }

    const localTarget = document.querySelector(href);

    if (!localTarget && location.pathname !== "/") {
      setMobileMenuOpen(false);
      navigate({ pathname: "/", hash: href });
      return;
    }

    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      window.setTimeout(() => scrollToSection(href), 180);
      return;
    }

    scrollToSection(href);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (mobileMenuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
    return undefined;
  }, [mobileMenuOpen]);

  // Close drawer whenever the route changes.
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMobileGroup(null);
  }, [location.pathname]);

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        initial={false}
        animate={{
          paddingTop: scrolled ? 8 : 16,
          paddingBottom: scrolled ? 8 : 16,
          backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,1)",
        }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-shadow duration-300 ${scrolled ? "shadow-[0_6px_24px_-12px_rgba(10,10,10,0.18)] border-b border-[#0A0A0A]/6" : "border-b border-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <a
              href="/"
              onClick={handleSectionClick("/")}
              className="flex items-center gap-3"
              data-testid="logo-link"
            >
              <motion.img
                src={KPL_LOGO}
                alt="KPL Logo"
                className="object-contain"
                animate={{ width: scrolled ? 36 : 48, height: scrolled ? 36 : 48 }}
                transition={{ duration: 0.25 }}
              />
              <motion.div
                className="hidden sm:block text-[#0A0A0A]"
                data-testid="brand-text-block"
                animate={{ opacity: scrolled ? 0.85 : 1 }}
                transition={{ duration: 0.25 }}
              >
                <span className="block font-bold text-sm uppercase tracking-wider">
                  Kenya Premier League
                </span>
                <span className="block text-[10px] font-medium uppercase tracking-[0.28em] text-[#0A0A0A]/55">
                  Since 2003
                </span>
              </motion.div>
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) =>
                link.children ? (
                  <div
                    key={link.label}
                    className="relative group"
                    data-testid={`nav-group-${toTestId(link.label)}`}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-medium text-sm uppercase tracking-wide text-[#0A0A0A] hover:text-[#D1111A] transition-colors"
                      data-testid={`nav-link-${toTestId(link.label)}`}
                    >
                      {link.label}
                      <ChevronDown
                        size={14}
                        className="transition-transform duration-200 group-hover:rotate-180"
                      />
                    </button>

                    <div className="pointer-events-none absolute left-0 top-full pt-4 opacity-0 -translate-y-2 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0">
                      <div className="min-w-[220px] border border-[#E4E4E7] bg-white p-2 shadow-xl">
                        {link.children.map((child) => (
                          <a
                            key={child.label}
                            href={child.href}
                            onClick={handleSectionClick(child.href)}
                            className="block px-4 py-3 text-sm uppercase tracking-[0.14em] text-[#0A0A0A]/72 hover:bg-[#F4F4F5] hover:text-[#D1111A] transition-colors"
                            data-testid={`nav-sublink-${toTestId(child.label)}`}
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={handleSectionClick(link.href)}
                    className="font-medium text-sm uppercase tracking-wide text-[#0A0A0A] hover:text-[#D1111A] transition-colors"
                    data-testid={`nav-link-${toTestId(link.label)}`}
                  >
                    {link.label}
                  </a>
                ),
              )}
            </div>

            {/* Animated hamburger button */}
            <button
              type="button"
              className="lg:hidden relative flex items-center justify-center w-10 h-10 -mr-2 text-[#0A0A0A]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileMenuOpen ? (
                  <motion.span
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <X size={24} strokeWidth={2} />
                  </motion.span>
                ) : (
                  <motion.span
                    key="open"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Menu size={24} strokeWidth={2} />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile drawer + backdrop */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-[#0A0A0A]/45 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <motion.aside
              key="drawer"
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ type: "tween", duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-white border-b border-[#E4E4E7] shadow-2xl pt-20 pb-6 max-h-[100vh] overflow-y-auto"
              data-testid="mobile-menu-drawer"
              role="dialog"
              aria-modal="true"
            >
              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.04, delayChildren: 0.05 } },
                }}
                className="px-6 flex flex-col gap-2"
              >
                {mobileNavLinks.map((link) =>
                  link.children ? (
                    <motion.div
                      key={link.label}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        show: { opacity: 1, y: 0 },
                      }}
                      className="border-b border-[#E4E4E7] last:border-b-0"
                      data-testid={`mobile-nav-group-${toTestId(link.label)}`}
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between py-4 text-[#0A0A0A] font-semibold uppercase tracking-[0.14em] text-sm"
                        onClick={() =>
                          setOpenMobileGroup(openMobileGroup === link.label ? null : link.label)
                        }
                        data-testid={`mobile-nav-link-${toTestId(link.label)}`}
                        aria-expanded={openMobileGroup === link.label}
                      >
                        <span>{link.label}</span>
                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-200 ${openMobileGroup === link.label ? "rotate-180 text-[#D1111A]" : "text-[#0A0A0A]/50"}`}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {openMobileGroup === link.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="pl-3 pr-4 pb-3 flex flex-col gap-1 border-l-2 border-[#D1111A]/25">
                              {link.children.map((child) => (
                                <a
                                  key={child.label}
                                  href={child.href}
                                  className="px-4 py-2.5 text-sm text-[#0A0A0A]/75 hover:text-[#D1111A] hover:bg-[#F4F4F5] transition-colors"
                                  onClick={handleSectionClick(child.href)}
                                  data-testid={`mobile-nav-sublink-${toTestId(child.label)}`}
                                >
                                  {child.label}
                                </a>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ) : (
                    <motion.a
                      key={link.label}
                      href={link.href}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        show: { opacity: 1, y: 0 },
                      }}
                      className="py-4 border-b border-[#E4E4E7] last:border-b-0 text-[#0A0A0A] font-semibold uppercase tracking-[0.14em] text-sm hover:text-[#D1111A] transition-colors"
                      onClick={handleSectionClick(link.href)}
                      data-testid={`mobile-nav-link-${toTestId(link.label)}`}
                    >
                      {link.label}
                    </motion.a>
                  ),
                )}
              </motion.div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
